import React, { useRef } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";

import { TinySliderNav, SlideItem } from "@euroland/react-tiny-slider";
import { classNames, formatDateTime, formatNumberBySettings } from "../utils";
import Tag from "./ui/Tag";
import Badge from "./ui/Badge";
import useInstruments, { useInstrument } from "../hooks/useInstruments";
import { useCompanySettings, useFormatSetting } from "../hooks/useCompanySettings";
import i18n from "../locale/i18n";
import { MARKET_STATUS, POSITIVE_CHANGE_NUMBER_FORMAT } from "../constant/common";
import { getCurrencyLabel } from "../store/utils";
import { instrumentDataAndSettingSelector } from "../store/slice/trade";

export default function MultipleTickerSlider() {
  const [{ instruments }] = useInstruments();
  const [{ selectedInstrument }, { onChangeSelectedInstrument }] = useInstrument();
  const tinySliderRef = useRef();
  let activeOrder = instruments.findIndex(
    instrument => instrument.instrumentId === selectedInstrument.instrumentId
  );
  activeOrder = activeOrder === -1 ? 0 : activeOrder;
  return (
    <>
      <div
        className="MultipleTickerSlider"
      >
        <TinySliderNav
          ref={tinySliderRef}
          isRtl={window.appSettings.isRtl}
          settings={{ gutter: 0 }}
          activeOrder={activeOrder}
          role="listbox"
          aria-orientation="horizontal"
          tabIndex={-1}
          aria-label={i18n.translate("tickerAriaLabel")}
        >
          {instruments.map((ticker, index) => (
            <TickerItem
              sliderIndex={index}
              key={index}
              active={ticker.instrumentId === selectedInstrument.instrumentId}
              onClick={() => {
                // tinySliderRef.current.goTo(index); // new version tiny will scroll to Item clicked
                onChangeSelectedInstrument(ticker.instrumentId);
              }}
              ticker={ticker}
            />
          ))}
        </TinySliderNav>
      </div>
    </>
  );
}

/**
 *
 * @param {{
 *  active: boolean,
 *  ticker: import("../..").IInstrument,
 *  onClick: () => void,
 * }} props
 * @returns
 */
export function TickerItem({ active, ticker, onClick, sliderIndex }) {
  const settings = useCompanySettings();
  const format = useFormatSetting();
  const instrumentDataAndSetting = useSelector(instrumentDataAndSettingSelector);
  const dateTimeValue =
    instrumentDataAndSetting[ticker.instrumentId].setting.limitStartingData || ticker.startingDate;
  const dateTimeFormat = dateTimeValue ? formatDateTime(dateTimeValue, settings) : i18n.translate("hyphen");
  const decimalDigitsSetting = instrumentDataAndSetting[ticker.instrumentId].setting.decimalDigits || format.decimalDigits;
  return (
    <SlideItem
      className={classNames("TickerItem", { active })}
      onClick={onClick}
      sliderIndex={sliderIndex}
      role="option"
      aria-selected={active}
    >
      <div className="TickerItem__container">
        <div className="title-wrapper">
          <div>
            <Badge active={ticker.marketStatus === MARKET_STATUS.OPEN} />
          </div>
          <h4 title={ticker.ticker}>{ticker.ticker}</h4>
          <Tag label={ticker.marketAbbreviation} aria-label={ticker.marketName} role="note" />
        </div>
        <div className="value-wrapper">
          <h2 className="value">
            {formatNumberBySettings(ticker.last, { ...format, decimalDigits: decimalDigitsSetting })}
          </h2>
          <p className="currency">{getCurrencyLabel(ticker.currencyCode)}</p>
        </div>
        <div
          className={classNames("change-wrapper", {
            "indicator--up": ticker.change > 0,
            "indicator--down": ticker.change < 0,
            "indicator--natural": ticker.change === 0,
          })}
        >
          {/* <Icon name={ticker.change >= 0 ? "arrow-up" : "arrow-down"} className="indicator--icon"></Icon> */}
          <p
            className={classNames("change-value", {
              "change-up": ticker.change >= 0,
              "change-down": ticker.change < 0,
            })}
          >
            {formatNumberBySettings(ticker.change, {
              ...format,
              decimalDigits: decimalDigitsSetting,
              positiveFormatPattern: settings.format?.positiveChangeFormat || POSITIVE_CHANGE_NUMBER_FORMAT,
            })}
          </p>
          <p
            className={classNames("change-percent", {
              "change-up": ticker.changePercentage >= 0,
              "change-down": ticker.changePercentage < 0,
            })}
          >
            (
            {formatNumberBySettings(ticker.changePercentage, {
              ...format,
              decimalDigits: decimalDigitsSetting,
              positiveFormatPattern: settings.format?.positiveChangeFormat || POSITIVE_CHANGE_NUMBER_FORMAT,
            })}
            %)
          </p>
        </div>
        <div className="dateTime-wrapper">
          <span className="dateTime-wrapper__label">{i18n.translate("dataAvailability")}</span>
          <span className="dateTime-wrapper__time">{dateTimeFormat}</span>
        </div>
      </div>
    </SlideItem>
  );
}

MultipleTickerSlider.propTypes = {};
TickerItem.prototype = {
  ticker: PropTypes.exact({
    id: PropTypes.number,
    default: PropTypes.bool,
    ticker: PropTypes.string,
    marketName: PropTypes.string,
    currencySign: PropTypes.string,
    numberOfTrade: PropTypes.number,
  }),
  onClick: PropTypes.func,
};
